<script>
  import { onMount } from 'svelte';
  import { draw } from 'svelte/transition';

  import * as d3 from 'd3';

  import rawdata from '../data/deathperhour';
  import mode from '../data/mode';

  let selection = [];
  let tooltip = 22;
  let w = 800,
    h = 200;
  const padding = {
    top: 20,
    right: 0,
    bottom: 20,
    left: 0,
  };
  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const xTicks = [10, 16, 22, 28];

  $: tooltipdata = ((rawdata) => {
    const res = rawdata
      .filter(
        (d) =>
          d.timeOfDay === tooltip ||
          ((d.timeOfDay - 6) % 24) + 5 === tooltip ||
          d.timeOfDay === 30
      )
      .slice(0, 2);

    if (res.length === 1)
      res.push({ timeOfDay: 31, deathPerHour: 0.611111111 });

    return res;
  })(rawdata);
  // $: tooltiparea = `M${[
  //   [X(tooltipdata[0].timeOfDay), Y(tooltipdata[0].deathPerHour)],
  //   [X(tooltipdata[1].timeOfDay), Y(tooltipdata[1].deathPerHour)],
  //   [X(tooltipdata[1].timeOfDay), Y(0)],
  //   [X(tooltipdata[0].timeOfDay), Y(0)],
  // ]
  //   .map((coord) => coord.join(','))
  //   .join('L')}Z`;
  $: tooltiparea = `M${line(tooltipdata).slice(1)} ${[
    [X(tooltipdata[1].timeOfDay), Y(0)],
    [X(tooltipdata[0].timeOfDay), Y(0)],
  ]
    .map((coord) => coord.join(','))
    .join('L')}Z`;

  $: X = d3
    .scaleLinear()
    .domain([6, 30])
    .range([padding.left, w - padding.right]);
  $: Y = d3
    .scaleLinear()
    .domain([0, 1])
    .range([h - padding.bottom, padding.top]);
  $: day = `M${[
    [X(6), Y(0)],
    [X(18), Y(0)],
    [X(18), h],
    [X(6), h],
  ]
    .map((coord) => coord.join(','))
    .join('L')}Z`;
  $: night = `M${[
    [X(18), Y(0)],
    [X(30), Y(0)],
    [X(30), h],
    [X(18), h],
  ]
    .map((coord) => coord.join(','))
    .join('L')}Z`;
  $: line = d3
    .line()
    .x((d) => X(d.timeOfDay))
    .y((d) => Y(d.deathPerHour))
    .curve(d3.curveStepAfter);
  $: path = line(rawdata);
  $: area = `${path}L${X(30)},${Y(0)}L${X(0)},${Y(0)}Z`;

  $: motorbikedata = rawdata
    .slice()
    .map((d) => ({ ...d, deathPerHour: d.deathPerHour * mode.motorbike }));
  $: motorbikepath = line(motorbikedata);
  $: motorbikearea = `${motorbikepath}L${X(30)},${Y(0)}L${X(0)},${Y(0)}Z`;

  const formatDeathPerHour = d3.format('.2f');
</script>

<style lang="scss">
  .linechart-container {
    height: 200px;
  }

  svg {
    width: 100%;
    height: 100%;
  }

  .tick {
    & line {
      stroke: #aaa;
      stroke-dasharray: 2;
    }

    & text {
      fill: #666;
      text-anchor: start;
    }

    &-0 line {
      stroke-dasharray: 0;
    }
  }

  .x-axis .tick {
    & text {
      text-anchor: middle;
    }
  }

  .daynight {
    pointer-events: none;

    & path {
      opacity: 0.3;
    }

    & .day {
      fill: rgb(144, 196, 236);
    }

    & .night {
      fill: rgb(29, 38, 60);
    }
  }

  .path-line {
    fill: transparent;
    stroke: rgba(235, 188, 201, 1);

    transition: 0.4s;

    &--motorbike {
      stroke: rgba(224, 123, 74, 1);
    }
  }

  .path-area {
    fill: rgba(235, 188, 201, 0.25);

    transition: 0.4s;

    &--motorbike {
      fill: rgba(224, 123, 74, 0.25);
    }
  }

  .summary {
    font-size: 1.6rem;
    text-align: center;
    margin-bottom: 2rem;

    & .measure {
      color: white;
      padding: 0.2rem 0.4rem;
      background-color: darkred;
    }
  }

  .graphics {
    position: relative;
  }

  .legend {
    display: flex;
    align-items: center;

    & + & {
      margin-left: 2rem;
    }

    &--box {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;
      padding: 1rem 1.5rem;
      background-color: rgba(255, 255, 255, 1);
    }

    &::before {
      content: '';
      width: 1rem;
      height: 1rem;
      display: block;
      margin-right: 0.5rem;
    }

    &--motorbike::before {
      background-color: rgba(224, 123, 74, 0.25);
      border: 1px solid rgba(224, 123, 74, 1);
    }

    &--others::before {
      background-color: rgba(235, 188, 201, 0.25);
      border: 1px solid rgba(235, 188, 201, 1);
    }
  }
</style>

<p class="summary">
  {#if tooltipdata}
    ที่เวลา
    <span class="measure">
      {tooltipdata[0].timeOfDay % 24}:00น. - {(tooltipdata[0].timeOfDay + 1) % 24}:00น.
    </span>
    มีจำนวนผู้เสียชีวิตเฉลี่ย
    <span class="measure">
      {formatDeathPerHour(tooltipdata[0].deathPerHour)}
    </span>
    คนต่อชั่วโมง
  {/if}
</p>

<div class="graphics">
  <div
    class="linechart-container"
    bind:clientWidth={w}
    bind:clientHeight={h}
    on:mousemove={(e) => (tooltip = ~~X.invert(e.offsetX))}>
    <svg>
      <!-- y axis -->
      <g class="axis y-axis" transform="translate(0, {padding.top})">
        {#each yTicks as tick}
          <g
            class="tick tick-{tick}"
            transform="translate(0, {Y(tick) - padding.bottom})">
            <line x2="100%" />
            <text y="-4">{tick}{tick === 1 ? ' คนต่อชั่วโมง' : ''}</text>
          </g>
        {/each}
      </g>

      <!-- x axis -->
      <g class="axis x-axis" transform="translate(0, {h})">
        {#each xTicks as tick}
          <g class="tick tick-{tick}" transform="translate({X(tick)}, 0)">
            <line y1="-{h}" y2="-{padding.bottom}" x1="0" x2="0" />
            <text y="-6">{tick % 24}:00</text>
          </g>
        {/each}
      </g>
      <g class="daynight">
        <path class="day" d={day} />
        <image
          href="./static/icon/sun.svg"
          x={X(12)}
          y={(Y(0) + h) / 2 - ((h - Y(0)) * 0.5) / 2}
          width={(h - Y(0)) * 0.5}
          height={(h - Y(0)) * 0.5} />
        <path class="night" d={night} />
        <image
          href="./static/icon/moon.svg"
          x={X(24) - ((h - Y(0)) * 0.5) / 2}
          y={(Y(0) + h) / 2 - ((h - Y(0)) * 0.5) / 2}
          width={(h - Y(0)) * 0.5}
          height={(h - Y(0)) * 0.5} />
      </g>

      <!-- path -->
      <path class="path-line" d={path} />
      <path class="path-area" d={area} />
      <path class="path-line path-line--motorbike" d={motorbikepath} />
      <path class="path-area path-area--motorbike" d={motorbikearea} />

      <!-- tooltip -->
      {#if tooltipdata}
        <path d={tooltiparea} fill="rgba(127, 23, 14, 1)" />
      {/if}
    </svg>
  </div>
</div>
<div class="legend--box">
  <div class="legend legend--motorbike">จักรยานยนต์</div>
  <div class="legend legend--others">ยานพาหนะอื่นๆ</div>
</div>
